import { useMutation } from "@tanstack/react-query"
import axios from "axios"
import * as toastr from "toastr"

import { useRegistrationData } from "../details/use-registration-data"
import { useOrderTicketQuestions } from "../tickets/use-order-ticket-questions"

import {
  transformAdvancedFormData,
  transformBasicFormData,
} from "utils/form-submission"

import {
  PlaceOrderFormData,
  BasicFormSubmissionData,
  AdvancedFormSubmissionData,
  OrderData,
} from "types"

interface UseSubmitFormMutationParams {
  orderUuid: string
  orderData?: OrderData
}

export const useSubmitFormMutation = ({
  orderUuid,
  orderData,
}: UseSubmitFormMutationParams) => {
  const { isAdvancedFormsEnabled } = useRegistrationData()

  // Fetch question data for all unique ticket types (memoized internally)
  const { questionsByTicketType } = useOrderTicketQuestions(orderData)

  return useMutation({
    mutationFn: async (formData: PlaceOrderFormData) => {
      if (!orderData) {
        throw new Error("Order data is required for form submission")
      }
      if (!orderData.ticket_set || orderData.ticket_set.length === 0) {
        throw new Error("No tickets found in order")
      }
      const transformedData:
        | AdvancedFormSubmissionData
        | BasicFormSubmissionData = isAdvancedFormsEnabled
        ? transformAdvancedFormData(formData, orderData, questionsByTicketType)
        : transformBasicFormData(formData, orderData)

      const response = await axios.post(
        `/api/registration/order/${orderUuid}/submit-answers/`,
        transformedData
      )
      return response.data
    },
    onError: (error) => {
      toastr.error("Failed to finalize order")
      console.error("Failed to submit order:", error)
    },
  })
}
